import { SLAViewDto } from '@issuemanager/api-client';
import {
  ConfirmationModal,
  StyledDataGrid,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import { handleSelectionChangeOnPaginatedData } from '@issuemanager/apps/client/utils';
import { useDeleteSLAs, useSLAs } from '@issuemanager/client/features/slas';
import { useEventCallback } from '@mui/material';
import type { GridSelectionModel } from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSLAsGridColumns, useSLAsGridQueryParams } from '../../hooks';
import { SLAPanel } from '../sla-panel';
import { SLAsEmptyState } from './empty-state';
import { SLAsGridToolbar } from './slas-grid-toolbar';

export function SLAsGrid() {
  const { t } = useTranslation(['settings']);
  const { enqueueSnackbar } = useSnackbar();
  const dataGridContext = useDataGridContext();
  const queryParams = useSLAsGridQueryParams(
    dataGridContext.filterModel,
    dataGridContext.sortModel,
  );

  const slas = useSLAs({
    page: dataGridContext.paginationModel.currentPage,
    limit: dataGridContext.paginationModel.pageSize,
    filter: queryParams.filter,
    sort: queryParams.sort,
  });

  const deleteSLA = useDeleteSLAs({
    onError: (error) => {
      enqueueSnackbar(error.response?.data.message, {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar(
        selectedSLAs.length > 1
          ? t('slaConfiguration.slaDeletedPlural')
          : t('slaConfiguration.slaDeleted'),
        {
          variant: 'success',
        },
      );
    },
  });

  const columns = useSLAsGridColumns();

  useEffect(() => {
    if (!slas.data) return;

    dataGridContext.setPaginationModel((prevState) => ({
      ...prevState,
      total: slas.data.meta.itemCount,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slas.data]);

  const [currentSLA, setCurrentSLA] = useState<SLAViewDto | null>(null);
  const [openSLAPanel, setOpenSLAPanel] = useState<boolean>(false);
  const [selectedSLAs, setSelectedSLAs] = useState<SLAViewDto[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (!openSLAPanel) {
      setCurrentSLA(null);
    }
  }, [openSLAPanel, setCurrentSLA]);

  const openConfirmationModal = useEventCallback(() => {
    setIsConfirmationModalOpen(true);
  });

  const closeConfirmationModal = useEventCallback(() => {
    setIsConfirmationModalOpen(false);
  });

  const handleRowClick = ({ row }: { row: SLAViewDto }) => {
    setCurrentSLA(row);
    setOpenSLAPanel(true);
  };

  const handleSelectionModelChange = useEventCallback(
    (selectionModel: GridSelectionModel) => {
      const selectedOnPage =
        slas?.data?.data.filter((sla) => selectionModel.includes(sla.id)) ?? [];
      const finalSelection = handleSelectionChangeOnPaginatedData(
        selectedSLAs,
        selectedOnPage,
        slas?.data?.data ?? [],
      );
      setSelectedSLAs(finalSelection);
    },
  );

  const handleEditClick = useEventCallback(() => {
    if (selectedSLAs.length !== 1) return;
    setCurrentSLA(selectedSLAs[0]);
    setOpenSLAPanel(true);
  });

  const handleUpdateSLASuccess = useEventCallback(() => {
    closeConfirmationModal();
    setSelectedSLAs([]);
  });

  const handleDeleteSLAAccept = useEventCallback(async () => {
    if (!selectedSLAs.length) return;
    await deleteSLA.mutateAsync({ ids: selectedSLAs.map((sla) => sla.id) });

    setSelectedSLAs(
      selectedSLAs.filter(
        (selectedSLA) => !selectedSLAs.some((sla) => sla.id === selectedSLA.id),
      ),
    );
    setCurrentSLA(null);
    closeConfirmationModal();
    setSelectedSLAs([]);
  });

  return (
    <>
      <div data-testid='SLAs-grid'>
        <StyledDataGrid
          error={slas.isError}
          loading={slas.isFetching}
          columns={columns}
          rows={slas.data?.data ?? []}
          checkboxSelection
          selectionModel={selectedSLAs.map((sla) => sla.id)}
          onSelectionModelChange={handleSelectionModelChange}
          components={{
            NoRowsOverlay: SLAsEmptyState,
            Toolbar: SLAsGridToolbar,
          }}
          componentsProps={{
            noRowsOverlay: {
              onAddSLAClick: () => setOpenSLAPanel(true),
            },
            toolbar: {
              onAddSLAClick: () => setOpenSLAPanel(true),
              selectedSLAs,
              onEditSLAClick: handleEditClick,
              onDeleteSLAClick: openConfirmationModal,
            },
          }}
          onRowClick={handleRowClick}
        />
      </div>

      <SLAPanel
        isOpen={openSLAPanel}
        setOpen={setOpenSLAPanel}
        sla={currentSLA}
        onSuccess={handleUpdateSLASuccess}
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onAccept={handleDeleteSLAAccept}
        onCancel={closeConfirmationModal}
        title={
          selectedSLAs?.length > 1
            ? t('slaConfiguration.cancelModal.titlePlural')
            : t('slaConfiguration.cancelModal.title')
        }
        description={
          selectedSLAs?.length > 1
            ? t('slaConfiguration.cancelModal.descriptionPlural')
            : t('slaConfiguration.cancelModal.description')
        }
        acceptButtonProps={{
          text: t('slaConfiguration.cancelModal.accept'),
          color: 'error',
          disabled: deleteSLA.isPending,
        }}
        cancelButtonProps={{
          text: t('slaConfiguration.cancelModal.cancel'),
          disabled: deleteSLA.isPending,
        }}
      />
    </>
  );
}
